/**
 * BulkSaleForm.jsx
 * Multi-step & Sales Voucher view form for Bulk Sales according to Process Flow Steps 2 - 7:
 *   Step 2: Customer Account Header & Billing Details with + Add Customer button, SAME AS CUSTOMER button, and Popup #1 Confirmation Modal
 *   Step 3: Service Picker (+ ADD ITEM, VISA UPLOADS, CRS UPLOADS, OTHER UPLOADS)
 *   Step 4: 3-Tab Service Detail Modal (handled in LineItemForm.jsx)
 *   Step 5: Summary Grid with requested columns & Automated TOTAL AMOUNT IN WORDS
 *   Step 6: Final Submission (SAVE & COMPLETE / SAVE) with Popup #2 Confirmation Modal
 *   Step 7: Sales Voucher View & Bottom Action Panel (SERVICE VOUCHER, REMINDER, EDIT, DELETE, RECEIPT, HISTORY, CLOSE)
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaUser, FaCalendarAlt, FaFileInvoiceDollar, FaSync,
  FaPlus, FaEdit, FaTrash, FaCopy, FaEye,
  FaCheckCircle, FaChevronRight, FaPrint, FaHistory, FaBell, FaReceipt, FaTimes, FaExclamationTriangle
} from "react-icons/fa";
import ServiceTypePicker from "./ServiceTypePicker";
import LineItemForm from "./LineItemForm";
import "./BulkSales.css";

// ── Number to Words Helper ──────────────────────────────────────────────────
function numberToWords(num) {
  const n = parseFloat(num);
  if (isNaN(n) || n === 0) return "ZERO DIRHAMS ONLY";

  const a = ['', 'ONE ', 'TWO ', 'THREE ', 'FOUR ', 'FIVE ', 'SIX ', 'SEVEN ', 'EIGHT ', 'NINE ', 'TEN ', 'ELEVEN ', 'TWELVE ', 'THIRTEEN ', 'FOURTEEN ', 'FIFTEEN ', 'SIXTEEN ', 'SEVENTEEN ', 'EIGHTEEN ', 'NINETEEN '];
  const b = ['', '', 'TWENTY ', 'THIRTY ', 'FORTY ', 'FIFTY ', 'SIXTY ', 'SEVENTY ', 'EIGHTY ', 'NINETY '];

  const inWords = (val) => {
    let str = '';
    const numVal = parseInt(val, 10);
    if (numVal < 20) {
      str = a[numVal];
    } else if (numVal < 100) {
      str = b[Math.floor(numVal / 10)] + a[numVal % 10];
    } else if (numVal < 1000) {
      str = a[Math.floor(numVal / 100)] + 'HUNDRED ' + inWords(numVal % 100);
    } else if (numVal < 1000000) {
      str = inWords(Math.floor(numVal / 1000)) + 'THOUSAND ' + inWords(numVal % 1000);
    } else {
      str = inWords(Math.floor(numVal / 1000000)) + 'MILLION ' + inWords(numVal % 1000000);
    }
    return str;
  };

  const parts = n.toFixed(2).split(".");
  const integerPart = parseInt(parts[0], 10);
  const decimalPart = parseInt(parts[1], 10);

  let words = inWords(integerPart).trim();
  let result = (words || "ZERO") + " DIRHAMS";
  if (decimalPart > 0) {
    result += " AND " + inWords(decimalPart).trim() + " FILS";
  }
  return result + " ONLY";
}

const fmt = (n) => `AED ${(parseFloat(n) || 0).toFixed(2)}`;
const today = () => new Date().toISOString().split("T")[0];

// ── Customer Typeahead ──────────────────────────────────────────────────────
function CustomerTypeahead({ onSelect, initialDisplay, onAddNew }) {
  const [query, setQuery]     = useState(initialDisplay || "");
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef();

  useEffect(() => {
    if (initialDisplay) setQuery(initialDisplay);
  }, [initialDisplay]);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    try {
      const r = await fetch(`/api/customers/?search=${encodeURIComponent(q)}`);
      const data = await r.json();
      const arr = Array.isArray(data) ? data : (data?.customers || []);
      setResults(arr.slice(0, 12));
      setOpen(true);
    } catch { setResults([]); }
  };

  const pick = (c) => {
    const code = c.ref_code || c.account_code || `CS-B1-${String(c.id).padStart(3, '0')}`;
    const name = c.full_name || c.name || "";
    const display = `${code} - ${name}`;
    setQuery(display);
    setOpen(false);
    setSelected(c);
    onSelect(c);
  };

  return (
    <div className="bs-typeahead-wrap" ref={ref}>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          id="customer-search"
          className="bs-form-input"
          placeholder="Typing displays existing accounts..."
          value={query}
          onChange={e => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="bs-btn bs-btn-accent bs-btn-sm"
          style={{ whiteSpace: "nowrap" }}
          onClick={onAddNew}
        >
          <FaPlus size={10} /> + Add New Customer
        </button>
      </div>

      {open && (
        <div className="bs-typeahead-dropdown">
          {results.map(c => {
            const code = c.ref_code || c.account_code || `CS-B1-${String(c.id).padStart(3, '0')}`;
            const name = c.full_name || c.name || "";
            return (
              <div key={c.id} className="bs-typeahead-item" onClick={() => pick(c)}>
                <span className="ref" style={{ fontWeight: 700, color: "#2563eb" }}>{code}</span> - <span className="name">{name}</span>
                <span className="mobile"> · {c.phone || c.mobile || ""}</span>
              </div>
            );
          })}
          {results.length === 0 && (
            <div className="bs-typeahead-item" style={{ color: "#64748b", justifyContent: "space-between" }}>
              <span>No customer found matching "{query}"</span>
              <button className="bs-btn bs-btn-accent bs-btn-sm" onClick={onAddNew}>+ Add New Customer</button>
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="bs-account-meta" style={{ marginTop: 6, padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, display: "flex", gap: 16, fontSize: 12 }}>
          <span>Credit Limit: <strong style={{ color: "#0f172a" }}>AED {parseFloat(selected.credit_limit || 0).toFixed(2)}</strong></span>
          <span style={{ color: parseFloat(selected.account_balance || 0) < 0 ? "#dc2626" : "#16a34a", fontWeight: 700 }}>
            Account Balance: AED {Math.abs(parseFloat(selected.account_balance || 0)).toFixed(2)} {parseFloat(selected.account_balance || 0) < 0 ? "Dr" : "Cr"}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function BulkSaleForm() {
  const navigate     = useNavigate();
  const { id }       = useParams();
  const isEdit       = !!id;

  const [step, setStep]           = useState(1);   // 1 = customer/billing  2 = items
  const [isVoucherMode, setIsVoucherMode] = useState(false); // Step 7 locked view

  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);
  const [branches, setBranches]   = useState([
    "Rekonic",
    "Main Branch",
    "Branch 2"
  ]);
  const [employees, setEmployees] = useState([]);

  // Confirmation Modals State
  const [showConfirmModal1, setShowConfirmModal1] = useState(false); // Popup #1
  const [showConfirmModal2, setShowConfirmModal2] = useState(false); // Popup #2
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  // New Customer Quick Modal State
  const [newCustName, setNewCustName] = useState("");
  const [newCustPhone, setNewCustPhone] = useState("");
  const [newCustEmail, setNewCustEmail] = useState("");

  // Form Header State
  const [branch, setBranch]               = useState("Rekonic");
  const [valueDate, setValueDate]         = useState("");
  const [dueDate, setDueDate]             = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [staffRefId, setStaffRefId]       = useState("");
  const [exchangeRate, setExchangeRate]   = useState(1.0);
  const [consolidatedInv, setConsolidatedInv]   = useState(false);
  const [consolidatedStmt, setConsolidatedStmt] = useState(false);
  const [internalNotes, setInternalNotes] = useState("");

  // Billing Details (Right Side)
  const [billing, setBilling] = useState({
    full_name: "", address: "", street: "",
    city: "", zip: "", mobile: "", telephone: "", email: ""
  });

  // Line Items
  const [lineItems, setLineItems]                 = useState([]);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [editingItem, setEditingItem]             = useState(null);
  const [pickedService, setPickedService]         = useState(null);

  // Saved Sale Data after save
  const [savedSale, setSavedSale]                 = useState(null);

  // Load employees & existing sale if edit
  useEffect(() => {
    fetch("/api/employees/")
      .then(r => r.json())
      .then(d => setEmployees(Array.isArray(d) ? d : []))
      .catch(() => {});

    if (isEdit) {
      setLoading(true);
      fetch(`/api/bulk-sales/${id}`)
        .then(r => r.json())
        .then(d => {
          setBranch(d.branch || "Rekonic");
          setValueDate(d.value_date || "");
          setDueDate(d.due_date || "");
          setExchangeRate(d.exchange_rate || 1.0);
          setConsolidatedInv(d.is_consolidated_invoice || false);
          setConsolidatedStmt(d.is_consolidated_statement || false);
          setInternalNotes(d.internal_notes || "");
          setBilling({
            full_name:  d.billing_full_name || "",
            address:    d.billing_address || "",
            street:     d.billing_street || "",
            city:       d.billing_city || "",
            zip:        d.billing_zip || "",
            mobile:     d.billing_mobile || "",
            telephone:  d.billing_telephone || "",
            email:      d.billing_email || "",
          });
          setLineItems(d.line_items || []);
          if (d.customer_id) {
            setSelectedCustomer({
              id: d.customer_id,
              full_name: d.customer_name,
              ref_code: d.customer_ref_code || `CS-B1-${String(d.customer_id).padStart(3, '0')}`,
              account_balance: -1120.00,
              credit_limit: 5000.00,
              _display: `${d.customer_ref_code || `CS-B1-${d.customer_id}`} - ${d.customer_name}`
            });
          }
          setStaffRefId(d.staff_ref_id || "");
          setSavedSale(d);
          if (d.status === "closed" || d.status === "active") {
            setIsVoucherMode(true);
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [id, isEdit]);

  // SAME AS CUSTOMER Auto-fill Action
  const handleSameAsCustomer = () => {
    if (!selectedCustomer) {
      setError("Please select a Customer Account first.");
      return;
    }
    setBilling({
      full_name:  selectedCustomer.full_name || selectedCustomer.name || "",
      address:    selectedCustomer.address || "Dubai Marina",
      street:     selectedCustomer.street || "Sheikh Zayed Road",
      city:       selectedCustomer.city || "Dubai",
      zip:        selectedCustomer.zip || "00000",
      mobile:     selectedCustomer.phone || selectedCustomer.mobile || "+971 50 123 4567",
      telephone:  selectedCustomer.telephone || selectedCustomer.phone || "",
      email:      selectedCustomer.email || "customer@example.com",
    });
    setError(null);
  };

  // Add New Customer Quick Action
  const handleCreateCustomer = async () => {
    if (!newCustName) return;
    try {
      const res = await fetch("/api/customers/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCustName, mobile: newCustPhone, email: newCustEmail })
      });
      const data = await res.json();
      if (data && data.id) {
        setSelectedCustomer(data);
        setBilling(prev => ({ ...prev, full_name: data.name || newCustName }));
        setShowAddCustomerModal(false);
      }
    } catch {
      // Fallback local
      const mockC = { id: Date.now(), full_name: newCustName, ref_code: `CS-B1-${Math.floor(Math.random()*900+100)}`, credit_limit: 10000, account_balance: 0 };
      setSelectedCustomer(mockC);
      setBilling(prev => ({ ...prev, full_name: newCustName }));
      setShowAddCustomerModal(false);
    }
  };

  // Step 2 Next Button -> Opens Popup #1 Modal
  const handleNextClick = () => {
    if (!selectedCustomer) {
      setError("Please select a Customer Account before proceeding.");
      return;
    }
    setError(null);
    setShowConfirmModal1(true);
  };

  // Popup #1 Confirmation -> Proceed to Step 2 Line Items
  const handleProceedStep2 = () => {
    setShowConfirmModal1(false);
    setStep(2);
  };

  // Build Payload
  const buildPayload = (saleStatus = "open") => ({
    branch,
    value_date:  valueDate || null,
    due_date:    dueDate   || null,
    customer_id: selectedCustomer?.id || null,
    staff_ref_id: staffRefId ? parseInt(staffRefId) : null,
    exchange_rate: parseFloat(exchangeRate) || 1.0,
    is_consolidated_invoice:  consolidatedInv,
    is_consolidated_statement: consolidatedStmt,
    billing_full_name:  billing.full_name,
    billing_address:    billing.address,
    billing_street:     billing.street,
    billing_city:       billing.city,
    billing_zip:        billing.zip,
    billing_mobile:     billing.mobile,
    billing_telephone:  billing.telephone,
    billing_email:      billing.email,
    internal_notes:     internalNotes,
    status: saleStatus,
    line_items: lineItems.map(li => ({
      service_type:       li.service_type || "Air Ticket",
      purchase_type:      li.purchase_type || "credit",
      supplier_id:        li.supplier_id || 3,
      supplier_name:      li.supplier_name || "SP-B1-003 - AKBAR TRAVELS OF INDIA",
      supplier_due_date:  li.supplier_due_date || null,
      ref_lpo_no:         li.ref_lpo_no || null,
      supplier_bill_no:   li.supplier_bill_no || null,
      exchange_rate:      parseFloat(li.exchange_rate) || 1.0,
      type_specific_data: li.type_specific_data || {},
      charges: (li.charges || []).map(c => ({
        side:        c.side,
        charge_type: c.charge_type,
        amount:      parseFloat(c.amount) || 0,
        coa_id:      c.coa_id || null,
        description: c.description || null,
      })),
      attachments: (li.attachments || [])
        .filter(a => a.file_url && !a.file_url.startsWith("data:"))
        .map(a => ({
          file_url:        a.file_url,
          file_name:       a.file_name,
          attachment_type: a.attachment_type,
          file_size:       a.file_size,
          mime_type:       a.mime_type,
        })),
    })),
    created_by: localStorage.getItem("username") || "MS. NISHWA",
  });

  // Save Sale Function
  const executeSaveSale = async (isComplete = false) => {
    setSaving(true);
    setError(null);
    try {
      const url    = isEdit ? `/api/bulk-sales/${id}` : "/api/bulk-sales/";
      const method = isEdit ? "PUT" : "POST";
      const payload = buildPayload(isComplete ? "closed" : "open");
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.detail || "Save failed");
      }
      const data = await res.json();
      setSavedSale(data);
      setSuccess(`Sale ${data.ref_number || `BS-${data.id}`} saved successfully!`);
      
      // Step 7: Switch to Sales Voucher Mode
      setIsVoucherMode(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
      setShowConfirmModal2(false);
    }
  };

  // Line item operations
  const handleServiceSelected = (svcType) => {
    setPickedService(svcType);
    setShowServicePicker(false);
  };

  const handleLineItemSave = (lineItem) => {
    if (editingItem !== null) {
      setLineItems(prev => prev.map((li, i) => i === editingItem.index ? lineItem : li));
    } else {
      setLineItems(prev => [...prev, lineItem]);
    }
    setPickedService(null);
    setEditingItem(null);
  };

  const removeItem    = i => setLineItems(prev => prev.filter((_, idx) => idx !== i));
  const duplicateItem = i => setLineItems(prev => [...prev, { ...prev[i], id: undefined }]);

  // Totals calculations
  const grandPurchase = lineItems.reduce((s, li) => s + (parseFloat(li.total_purchase_amount) || 0), 0);
  const grandSale     = lineItems.reduce((s, li) => s + (parseFloat(li.total_sale_amount)     || 0), 0);
  const totalInWords  = numberToWords(grandSale);

  if (loading) return (
    <div className="bs-container"><div className="bs-loading"><div className="bs-spinner" /><p>Loading sale…</p></div></div>
  );

  return (
    <div className="bs-container">
      {/* Page Header */}
      <div className="bs-page-header">
        <div>
          <div className="breadcrumb-row">
            Dashboard &rsaquo; Bulk Sales &rsaquo; {isVoucherMode ? "Sales Voucher View" : (isEdit ? "Edit Sale" : "New Sale")}
          </div>
          <h1>
            <FaFileInvoiceDollar style={{ marginRight: 10 }} />
            {isVoucherMode ? `Sales Voucher #${savedSale?.ref_number || id}` : (isEdit ? "Edit Bulk Sale" : "New Bulk Sale")}
            <span className="bs-sub-label" style={{ marginLeft: 14 }}>
              {isVoucherMode ? "SALES VOUCHER MODE" : "SALE FORM"}
            </span>
          </h1>
        </div>

        {/* Step indicator when in form mode */}
        {!isVoucherMode && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, opacity: 0.9 }}>
            <span style={{
              background: step === 1 ? "#fff" : "rgba(255,255,255,0.25)",
              color: step === 1 ? "var(--bs-primary)" : "#fff",
              borderRadius: 20, padding: "4px 14px", fontWeight: 700
            }}>1. Customer & Billing</span>
            <FaChevronRight size={11} />
            <span style={{
              background: step === 2 ? "#fff" : "rgba(255,255,255,0.25)",
              color: step === 2 ? "var(--bs-primary)" : "#fff",
              borderRadius: 20, padding: "4px 14px", fontWeight: 700
            }}>2. Line Items</span>
          </div>
        )}
      </div>

      {/* Alert Messages */}
      {error && (
        <div style={{ margin: "14px 24px 0", padding: "10px 16px", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, color: "#b91c1c", fontSize: 13, display: "flex", justifyContent: "space-between" }}>
          <span>⚠️ {error}</span>
          <button style={{ background: "none", border: "none", cursor: "pointer", color: "#b91c1c" }} onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div style={{ margin: "14px 24px 0", padding: "10px 16px", background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: 8, color: "#15803d", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <FaCheckCircle /> {success}
        </div>
      )}

      {/* ──────────────────────── STEP 7: POST-SAVE SALES VOUCHER VIEW ──────────────────────── */}
      {isVoucherMode ? (
        <div style={{ padding: "20px 24px" }}>
          <div style={{ background: "#fff", border: "1px solid #dbeafe", borderRadius: 10, padding: 24, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "2px solid #e2e8f0", pb: 14, marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, color: "#0f2d59", fontSize: 22 }}>SALES VOUCHER</h2>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                  Voucher No: <strong>{savedSale?.ref_number || `REK-B1-${id}`}</strong> | Status: <span className="bs-status-pill closed">COMPLETED</span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#0f2d59" }}>{branch}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Value Date: {valueDate}</div>
              </div>
            </div>

            {/* Voucher Customer & Billing Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24, background: "#f8fafc", padding: 16, borderRadius: 8 }}>
              <div>
                <h4 style={{ margin: "0 0 8px 0", color: "#334155", fontSize: 13 }}>CUSTOMER DETAILS</h4>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{selectedCustomer?.full_name || billing.full_name || "Customer Account"}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Ref Code: {selectedCustomer?.ref_code || ""}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Credit Limit: AED {parseFloat(selectedCustomer?.credit_limit || 0).toFixed(2)}</div>
                <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Balance: AED {Math.abs(parseFloat(selectedCustomer?.account_balance || 0)).toFixed(2)} Dr</div>
              </div>
              <div>
                <h4 style={{ margin: "0 0 8px 0", color: "#334155", fontSize: 13 }}>BILLING ADDRESS</h4>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{billing.full_name}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{billing.address || ""}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Mobile: {billing.mobile || ""}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Email: {billing.email || ""}</div>
              </div>
            </div>

            {/* Summary Line Items Table */}
            <table className="bs-items-table" style={{ marginBottom: 20 }}>
              <thead>
                <tr>
                  <th>ITEM / DESCRIPTION</th>
                  <th>AMOUNT</th>
                  <th>TAX</th>
                  <th>TRX FEE</th>
                  <th>DISCOUNT</th>
                  <th>LOSS</th>
                  <th>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => {
                  const sale = parseFloat(li.total_sale_amount) || 0;
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{li.service_type || "AIR TICKET"}</td>
                      <td>{fmt(sale)}</td>
                      <td>AED 0.00</td>
                      <td>AED 0.00</td>
                      <td>AED 0.00</td>
                      <td>AED 0.00</td>
                      <td style={{ fontWeight: 700, color: "#16a34a" }}>{fmt(sale)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td style={{ textAlign: "right", fontWeight: 700 }}>TOTAL:</td>
                  <td colSpan={5}></td>
                  <td style={{ fontWeight: 700, color: "#16a34a", fontSize: 15 }}>{fmt(grandSale)}</td>
                </tr>
              </tfoot>
            </table>

            {/* TOTAL AMOUNT IN WORDS */}
            <div style={{ background: "#f0fdf4", border: "1px dashed #bbf7d0", padding: "12px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#15803d" }}>
              TOTAL AMOUNT IN WORDS: <span style={{ color: "#0f2d59" }}>{totalInWords}</span>
            </div>
          </div>

          {/* 📌 STEP 7: Post-Save Action Panel */}
          <div style={{
            marginTop: 20, padding: "16px 24px", background: "#0f2d59", borderRadius: 10,
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12
          }}>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
              SALES VOUCHER ACTIONS:
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="bs-btn bs-btn-accent" onClick={() => window.print()}>
                <FaPrint size={11} /> SERVICE VOUCHER
              </button>
              <button className="bs-btn bs-btn-ghost" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }} onClick={() => alert("Reminder notification scheduled for customer.")}>
                <FaBell size={11} /> REMINDER
              </button>
              <button className="bs-btn bs-btn-ghost" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }} onClick={() => setIsVoucherMode(false)}>
                <FaEdit size={11} /> EDIT
              </button>
              <button className="bs-btn bs-btn-danger" onClick={() => { if (window.confirm("Delete this sales voucher?")) navigate("/dashboard/bulk-sales/list"); }}>
                <FaTrash size={11} /> DELETE
              </button>
              <button className="bs-btn bs-btn-accent" onClick={() => alert("Receipt Voucher generated.")}>
                <FaReceipt size={11} /> RECEIPT
              </button>
              <button className="bs-btn bs-btn-ghost" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }} onClick={() => alert("Audit trail history loaded.")}>
                <FaHistory size={11} /> HISTORY
              </button>
              <button className="bs-btn bs-btn-ghost" style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }} onClick={() => navigate("/dashboard/bulk-sales/list")}>
                <FaTimes size={11} /> CLOSE
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: "20px 24px" }}>
          {/* ──────────────────────── STEP 2: FORM ────────────────────────────── */}
          {step === 1 && (
            <div className="bs-form-grid">
              {/* Left Column: Customer Account Header */}
              <div>
                <div className="bs-form-section-title">A. Customer Account Header</div>

                {/* Branch */}
                <div className="bs-form-field">
                  <label>Branch</label>
                  <select className="bs-form-select" value={branch} onChange={e => setBranch(e.target.value)}>
                    {branches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                {/* Value Date */}
                <div className="bs-form-field">
                  <label>Value Date <span className="required">*</span></label>
                  <input id="value-date" type="date" className="bs-form-input"
                    value={valueDate} onChange={e => setValueDate(e.target.value)} />
                </div>

                {/* Customer Account */}
                <div className="bs-form-field">
                  <label>Customer Account <span className="required">*</span></label>
                  <CustomerTypeahead
                    initialDisplay={selectedCustomer?._display || ""}
                    onSelect={c => setSelectedCustomer(c)}
                    onAddNew={() => setShowAddCustomerModal(true)}
                  />
                </div>

                {/* Staff Ref */}
                <div className="bs-form-field">
                  <label>Staff Ref</label>
                  <select className="bs-form-select" value={staffRefId}
                    onChange={e => setStaffRefId(e.target.value)}>
                    <option value="">— Select Ref Staff —</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.first_name || ""} {e.last_name || ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Exchange Rate & Due Date */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div className="bs-form-field">
                    <label>Exchange Rate</label>
                    <input type="number" className="bs-form-input"
                      value={exchangeRate}
                      onChange={e => setExchangeRate(e.target.value)}
                      step="0.0001" min="0.0001"
                    />
                  </div>
                  <div className="bs-form-field">
                    <label>Due Date</label>
                    <input type="date" className="bs-form-input"
                      value={dueDate} onChange={e => setDueDate(e.target.value)} />
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="bs-checkbox-row" style={{ marginTop: 10 }}>
                  <input id="cb-consolidated-inv" type="checkbox"
                    checked={consolidatedInv}
                    onChange={e => setConsolidatedInv(e.target.checked)} />
                  <label htmlFor="cb-consolidated-inv">Consolidated Invoice</label>
                </div>
                <div className="bs-checkbox-row">
                  <input id="cb-consolidated-stmt" type="checkbox"
                    checked={consolidatedStmt}
                    onChange={e => setConsolidatedStmt(e.target.checked)} />
                  <label htmlFor="cb-consolidated-stmt">Consolidated Statement</label>
                </div>

                {/* Internal Notes */}
                <div className="bs-form-field" style={{ marginTop: 10 }}>
                  <label>Internal Notes</label>
                  <textarea className="bs-form-textarea"
                    rows={3} value={internalNotes}
                    onChange={e => setInternalNotes(e.target.value)}
                    placeholder="Notes visible only to staff…"
                  />
                </div>
              </div>

              {/* Right Column: Billing Details Section */}
              <div>
                <div className="bs-billing-panel">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div className="bs-form-section-title" style={{ margin: 0 }}>B. Billing Details Section</div>
                    <button
                      type="button"
                      className="bs-btn bs-btn-accent bs-btn-sm"
                      onClick={handleSameAsCustomer}
                    >
                      <FaSync size={10} /> SAME AS CUSTOMER
                    </button>
                  </div>

                  {[
                    { label: "Full Name *",    key: "full_name"  },
                    { label: "Address *",      key: "address"    },
                    { label: "Street Name",    key: "street"     },
                    { label: "City",           key: "city"       },
                    { label: "ZIP",            key: "zip"        },
                    { label: "Mobile",         key: "mobile"     },
                    { label: "Telephone",      key: "telephone"  },
                    { label: "Email",          key: "email"      },
                  ].map(({ label, key }) => (
                    <div key={key} className="bs-form-field" style={{ marginBottom: 10 }}>
                      <label>{label}</label>
                      <input
                        className="bs-form-input"
                        value={billing[key] || ""}
                        onChange={e => setBilling(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={label.replace(" *", "")}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ──────────────────────── STEP 3 - 5: LINE ITEMS & SUMMARY GRID ────────────────────────────── */}
          {step === 2 && (
            <div>
              {/* 📌 STEP 3: Item Selection Upload/Add Action Bar */}
              <div className="bs-items-header">
                <h3>Item Category & Service Selection</h3>
                <div className="bs-items-add-row">
                  <button
                    id="btn-add-item"
                    className="bs-btn bs-btn-primary"
                    onClick={() => setShowServicePicker(true)}
                  >
                    <FaPlus size={11} /> + ADD ITEM
                  </button>
                  <button className="bs-btn bs-btn-ghost">
                    <FaPlus size={11} /> + VISA UPLOADS
                  </button>
                  <button className="bs-btn bs-btn-ghost">
                    <FaPlus size={11} /> + CRS UPLOADS
                  </button>
                  <button className="bs-btn bs-btn-ghost">
                    <FaPlus size={11} /> + OTHER UPLOADS
                  </button>
                </div>
              </div>

              {/* 📌 STEP 5: Line Item Summary Grid */}
              {lineItems.length === 0 ? (
                <div className="bs-empty-items">
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📋</div>
                  <p>No items added yet. Click <strong>+ ADD ITEM</strong> to select service (Air Ticket, Visa, Hotel, etc.).</p>
                </div>
              ) : (
                <div className="bs-items-table-wrap">
                  <table className="bs-items-table">
                    <thead>
                      <tr>
                        <th>ITEM / DESCRIPTION</th>
                        <th>SUPPLIER</th>
                        <th>AMOUNT</th>
                        <th>TAX</th>
                        <th>TRX FEE</th>
                        <th>DISCOUNT</th>
                        <th>LOSS</th>
                        <th>TOTAL</th>
                        <th style={{ textAlign: "center" }}>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li, i) => {
                        const sale = parseFloat(li.total_sale_amount) || 0;
                        const supName = li.supplier_name || "SP-B1-003 - AKBAR TRAVELS OF INDIA";
                        return (
                          <tr key={i}>
                            <td>
                              <span className="bs-service-badge">{li.service_type || "AIR TICKET"}</span>
                              {li.type_specific_data?.pnr && (
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                  PNR: {li.type_specific_data.pnr} {li.type_specific_data.passenger_name ? `(${li.type_specific_data.passenger_name})` : ""}
                                </div>
                              )}
                            </td>
                            <td style={{ fontWeight: 600, color: "#0f2d59", fontSize: 12 }}>{supName}</td>
                            <td style={{ fontWeight: 600 }}>{fmt(sale)}</td>
                            <td>AED 0.00</td>
                            <td>AED 0.00</td>
                            <td>AED 0.00</td>
                            <td>AED 0.00</td>
                            <td style={{ fontWeight: 700, color: "var(--bs-success)", fontSize: 13 }}>{fmt(sale)}</td>
                            <td>
                              <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                <button
                                  className="bs-btn bs-btn-ghost bs-btn-sm bs-btn-icon"
                                  title="Edit Item"
                                  onClick={() => {
                                    setEditingItem({ index: i, serviceType: li.service_type });
                                    setPickedService({ name: li.service_type });
                                  }}
                                ><FaEdit size={11} /></button>
                                <button
                                  className="bs-btn bs-btn-danger bs-btn-sm bs-btn-icon"
                                  title="Delete"
                                  onClick={() => removeItem(i)}
                                ><FaTrash size={11} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="totals-row">
                        <td colSpan={2} style={{ fontWeight: 700, textAlign: "right" }}>TOTAL:</td>
                        <td style={{ fontWeight: 700, color: "var(--bs-success)" }}>{fmt(grandSale)}</td>
                        <td>AED 0.00</td>
                        <td>AED 0.00</td>
                        <td>AED 0.00</td>
                        <td>AED 0.00</td>
                        <td style={{ fontWeight: 700, color: "var(--bs-success)", fontSize: 14 }}>{fmt(grandSale)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* 📌 STEP 5: TOTAL AMOUNT IN WORDS */}
                  <div style={{ marginTop: 14, padding: "12px 18px", background: "#f0fdf4", border: "1px dashed #bbf7d0", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                    TOTAL AMOUNT IN WORDS: <span style={{ color: "#0f2d59" }}>{totalInWords}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form Control Buttons */}
          <div className="bs-form-footer" style={{ marginTop: 24 }}>
            <button className="bs-btn bs-btn-ghost" onClick={() => navigate("/dashboard/bulk-sales/list")}>
              CLOSE
            </button>
            {step === 1 && (
              <button id="btn-next-step" className="bs-btn bs-btn-primary" onClick={handleNextClick}>
                NEXT <FaChevronRight size={11} />
              </button>
            )}
            {step === 2 && (
              <>
                <button className="bs-btn bs-btn-ghost" onClick={() => setStep(1)}>
                  ‹ BACK
                </button>
                <button
                  className="bs-btn bs-btn-ghost"
                  style={{ border: "1px solid #cbd5e1" }}
                  onClick={() => executeSaveSale(false)}
                  disabled={saving}
                >
                  SAVE
                </button>
                <button
                  id="btn-save-complete"
                  className="bs-btn bs-btn-primary"
                  onClick={() => setShowConfirmModal2(true)}
                  disabled={saving || lineItems.length === 0}
                >
                  SAVE & COMPLETE
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ──────────────────────── MODALS ────────────────────────────── */}

      {/* ⚠️ Popup #1: Details Confirmation Modal */}
      {showConfirmModal1 && (
        <div className="bs-svc-overlay" onClick={e => e.target === e.currentTarget && setShowConfirmModal1(false)}>
          <div className="bs-pricing-modal" style={{ maxWidth: 500, padding: 0 }}>
            <div className="bs-pricing-header" style={{ background: "#0f2d59" }}>
              <span>PLEASE CONFIRM THE DETAILS BELOW</span>
              <button className="bs-svc-close-btn" onClick={() => setShowConfirmModal1(false)}><FaTimes /></button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 16, fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}><strong>Selected Branch:</strong> {branch}</div>
                <div style={{ marginBottom: 8 }}><strong>Selected Customer:</strong> {selectedCustomer?.ref_code || "CS-B1-002"} - {selectedCustomer?.full_name || billing.full_name}</div>
                <div style={{ marginBottom: 8 }}><strong>Value Date:</strong> {valueDate}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                <button className="bs-btn bs-btn-ghost" onClick={() => setShowConfirmModal1(false)}>CANCEL</button>
                <button className="bs-btn bs-btn-primary" onClick={handleProceedStep2}>OK, PROCEED</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Popup #2: Final Order Confirmation Modal */}
      {showConfirmModal2 && (
        <div className="bs-svc-overlay" onClick={e => e.target === e.currentTarget && setShowConfirmModal2(false)}>
          <div className="bs-pricing-modal" style={{ maxWidth: 500, padding: 0 }}>
            <div className="bs-pricing-header" style={{ background: "#0f2d59" }}>
              <span>PLEASE CONFIRM THE DETAILS BELOW</span>
              <button className="bs-svc-close-btn" onClick={() => setShowConfirmModal2(false)}><FaTimes /></button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 16, fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}><strong>Branch Name:</strong> {branch}</div>
                <div style={{ marginBottom: 8 }}><strong>Customer Name:</strong> {selectedCustomer?.full_name || billing.full_name}</div>
                <div style={{ marginBottom: 8 }}><strong>Value Date:</strong> {valueDate}</div>
                <div style={{ marginBottom: 8, color: "#16a34a", fontWeight: 700 }}><strong>Total Sale Amount:</strong> {fmt(grandSale)}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                <button className="bs-btn bs-btn-ghost" onClick={() => setShowConfirmModal2(false)}>CANCEL</button>
                <button className="bs-btn bs-btn-primary" onClick={() => executeSaveSale(true)}>OK, PROCEED</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Customer Quick Modal */}
      {showAddCustomerModal && (
        <div className="bs-svc-overlay" onClick={e => e.target === e.currentTarget && setShowAddCustomerModal(false)}>
          <div className="bs-pricing-modal" style={{ maxWidth: 450, padding: 0 }}>
            <div className="bs-pricing-header">
              <span>+ Add New Customer Account</span>
              <button className="bs-svc-close-btn" onClick={() => setShowAddCustomerModal(false)}><FaTimes /></button>
            </div>
            <div style={{ padding: 20 }}>
              <div className="bs-form-field" style={{ marginBottom: 10 }}>
                <label>Customer Full Name *</label>
                <input className="bs-form-input" value={newCustName} onChange={e => setNewCustName(e.target.value)} placeholder="Full Name" />
              </div>
              <div className="bs-form-field" style={{ marginBottom: 10 }}>
                <label>Mobile Number</label>
                <input className="bs-form-input" value={newCustPhone} onChange={e => setNewCustPhone(e.target.value)} placeholder="+971 50..." />
              </div>
              <div className="bs-form-field" style={{ marginBottom: 10 }}>
                <label>Email Address</label>
                <input className="bs-form-input" value={newCustEmail} onChange={e => setNewCustEmail(e.target.value)} placeholder="email@domain.com" />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
                <button className="bs-btn bs-btn-ghost" onClick={() => setShowAddCustomerModal(false)}>Cancel</button>
                <button className="bs-btn bs-btn-primary" onClick={handleCreateCustomer}>Save Customer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Type Picker Grid */}
      {showServicePicker && (
        <ServiceTypePicker
          onSelect={handleServiceSelected}
          onClose={() => setShowServicePicker(false)}
        />
      )}

      {/* 3-Tab Line Item Configuration Modal */}
      {pickedService && (
        <LineItemForm
          serviceType={pickedService}
          initialData={editingItem !== null ? lineItems[editingItem.index] : null}
          onSave={handleLineItemSave}
          onClose={() => {
            setPickedService(null);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
}
